import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle2, Loader2, Phone, Star, ShieldCheck, Clock } from "lucide-react";
import { toast } from "sonner";
import { submitEnquiry } from "../lib/enquiry";
import { CONTACT, IMAGES } from "../data/content";

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  check_in: "",
  check_out: "",
  guests: "2 Adults",
  message: "",
};

const inputCls =
  "w-full bg-transparent border-b border-[#D5D1C5] focus:border-[#243E2C] py-2.5 text-[#1C231F] placeholder-[#A7A99E] focus:outline-none transition-colors";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.18em] text-[#5A655E] mb-1 block">
      {label}
    </span>
    {children}
  </label>
);

export const EnquiryModal = ({ open, onClose, heading }) => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      const t = setTimeout(() => {
        setForm(EMPTY);
        setDone(false);
        setSubmitting(false);
      }, 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && open && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please share your name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await submitEnquiry(form);
      setDone(true);
      toast.success("Enquiry sent — we'll be in touch shortly.");
    } catch (err) {
      console.error(err);
      toast.error(
        "We couldn't send your enquiry right now. Please call us instead."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-6"
          data-testid="enquiry-modal"
        >
          <div
            className="absolute inset-0 bg-[#10180F]/80 backdrop-blur-sm"
            onClick={onClose}
            data-testid="enquiry-modal-overlay"
          />

          <motion.div
            initial={{ opacity: 0, y: 28, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.98 }}
            transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-3xl bg-[#F6F5F0] shadow-2xl overflow-hidden max-h-[94vh] grid grid-cols-1 md:grid-cols-2"
          >
            {/* Gold top accent */}
            <div className="absolute top-0 left-0 w-full h-[3px] bg-[#C4A575] z-10" />

            {/* Left visual panel */}
            <div className="relative hidden md:block">
              <img
                src={IMAGES.dining}
                alt="Nine Furlongs estate"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F1A11] via-[#0F1A11]/55 to-[#0F1A11]/25" />
              <div className="relative h-full flex flex-col justify-between p-8 text-[#EFECE4]">
                <div className="flex items-center gap-1 text-[#C4A575]">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={13} fill="#C4A575" strokeWidth={0} />
                  ))}
                  <span className="text-[11px] tracking-wide text-[#EFECE4]/80 ml-1">
                    Loved by guests
                  </span>
                </div>

                <div>
                  <p className="font-serif text-2xl leading-snug italic">
                    "The most peaceful days of our lives — above the clouds,
                    surrounded by forest."
                  </p>
                  <div className="mt-6 space-y-2.5 text-sm text-[#EFECE4]/85">
                    <p className="flex items-center gap-2.5">
                      <Clock size={15} className="text-[#C4A575]" /> Replies
                      within a few hours
                    </p>
                    <p className="flex items-center gap-2.5">
                      <ShieldCheck size={15} className="text-[#C4A575]" /> No spam,
                      ever — your details stay private
                    </p>
                    <a
                      href={CONTACT.phoneHref}
                      className="flex items-center gap-2.5 hover:text-[#C4A575] transition-colors"
                    >
                      <Phone size={15} className="text-[#C4A575]" /> {CONTACT.phone}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right form / success panel */}
            <div className="relative overflow-y-auto max-h-[94vh] p-7 sm:p-9">
              <button
                onClick={onClose}
                data-testid="enquiry-modal-close"
                aria-label="Close"
                className="absolute top-4 right-4 text-[#5A655E] hover:text-[#243E2C] transition-colors z-10"
              >
                <X size={22} />
              </button>

              <AnimatePresence mode="wait">
                {done ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-10 flex flex-col items-center justify-center min-h-[420px]"
                    data-testid="enquiry-modal-success"
                  >
                    <div className="w-16 h-16 rounded-full bg-[#243E2C]/8 flex items-center justify-center">
                      <CheckCircle2 size={40} strokeWidth={1.4} className="text-[#243E2C]" />
                    </div>
                    <h3 className="font-serif text-3xl text-[#243E2C] mt-6">
                      Thank you, {form.name.split(" ")[0]}.
                    </h3>
                    <p className="text-[#5A655E] mt-3 leading-relaxed max-w-xs">
                      Your enquiry is on its way to our reservations team. We'll
                      reach out shortly. For an instant response, call us.
                    </p>
                    <a
                      href={CONTACT.phoneHref}
                      className="mt-7 inline-flex items-center gap-2 bg-[#243E2C] text-white px-7 py-3.5 uppercase text-sm tracking-[0.18em] hover:bg-[#1A2E20] transition-colors"
                      data-testid="enquiry-modal-call-button"
                    >
                      <Phone size={16} /> Call {CONTACT.phone}
                    </a>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    onSubmit={handleSubmit}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="space-y-5"
                    data-testid="enquiry-modal-form"
                  >
                    <div>
                      <span className="inline-flex items-center gap-1.5 bg-[#C4A575]/15 text-[#8a6d3b] text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 mb-4 font-semibold">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C4A575] animate-pulse" />
                        Now taking bookings
                      </span>
                      <p className="uppercase text-[11px] tracking-[0.22em] text-[#C4A575] font-semibold">
                        Reservations
                      </p>
                      <h3 className="font-serif text-3xl sm:text-[2rem] text-[#243E2C] mt-1.5 leading-tight">
                        {heading || "Plan your stay"}
                      </h3>
                      <p className="text-sm text-[#5A655E] mt-2">
                        Share a few details and our team will craft your
                        itinerary.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field label="Full Name *">
                        <input
                          data-testid="modal-name-input"
                          value={form.name}
                          onChange={update("name")}
                          placeholder="Your name"
                          className={inputCls}
                          required
                        />
                      </Field>
                      <Field label="Phone *">
                        <input
                          data-testid="modal-phone-input"
                          value={form.phone}
                          onChange={update("phone")}
                          placeholder="+91"
                          className={inputCls}
                          required
                        />
                      </Field>
                    </div>

                    <Field label="Email *">
                      <input
                        data-testid="modal-email-input"
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="you@email.com"
                        className={inputCls}
                        required
                      />
                    </Field>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <Field label="Check In">
                        <input
                          data-testid="modal-checkin-input"
                          type="date"
                          value={form.check_in}
                          onChange={update("check_in")}
                          className={inputCls}
                        />
                      </Field>
                      <Field label="Check Out">
                        <input
                          data-testid="modal-checkout-input"
                          type="date"
                          value={form.check_out}
                          onChange={update("check_out")}
                          className={inputCls}
                        />
                      </Field>
                      <Field label="Guests">
                        <select
                          data-testid="modal-guests-input"
                          value={form.guests}
                          onChange={update("guests")}
                          className={`${inputCls} cursor-pointer`}
                        >
                          {[
                            "1 Adult",
                            "2 Adults",
                            "2 Adults + 1 Child",
                            "Family (4+)",
                            "Group",
                          ].map((g) => (
                            <option key={g}>{g}</option>
                          ))}
                        </select>
                      </Field>
                    </div>

                    <Field label="Message (optional)">
                      <textarea
                        data-testid="modal-message-input"
                        value={form.message}
                        onChange={update("message")}
                        rows={2}
                        placeholder="Tell us about your dream stay..."
                        className={`${inputCls} resize-none`}
                      />
                    </Field>

                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="modal-submit-button"
                      className="w-full bg-[#243E2C] text-white py-4 uppercase text-sm tracking-[0.2em] font-medium hover:bg-[#1A2E20] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={18} className="animate-spin" /> Sending…
                        </>
                      ) : (
                        "Send Enquiry"
                      )}
                    </button>
                    <p className="text-xs text-[#5A655E] text-center">
                      Prefer to talk?{" "}
                      <a href={CONTACT.phoneHref} className="text-[#243E2C] font-semibold underline underline-offset-2">
                        Call {CONTACT.phone}
                      </a>
                    </p>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
